import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchUserInactiveShipments,
  formatInactiveShipmentDateTime,
  getInactiveShipmentsRoute,
} from "../utils/inactiveShipmentAlert";

const InactiveShipmentAlertModal = () => {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [shipments, setShipments] = useState([]);

  const userId = Number(user?.user_id || user?.id || 0);
  const shipmentsRoute = useMemo(
    () => getInactiveShipmentsRoute(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (!userId) return;

    let active = true;

    const loadInactiveShipments = async () => {
      setLoading(true);

      try {
        const filtered = await fetchUserInactiveShipments(userId);

        if (!active) return;

        setShipments(filtered);

        if (!filtered.length) {
          setOpen(false);
          return;
        }

        setOpen(true);
      } catch (error) {
        console.error("Failed to load inactive shipment alert:", error);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadInactiveShipments();

    return () => {
      active = false;
    };
  }, [userId]);

  if (!userId || loading || !open || shipments.length === 0) {
    return null;
  }

  const previewShipments = shipments.slice(0, 5);
  const remainingCount = shipments.length - previewShipments.length;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 p-3 sm:p-4 backdrop-blur-[2px]">
      <div className="flex max-h-[min(92vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-5 sm:px-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
              Shipment Alert
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">
              Inactive shipments found
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              These shipments are assigned to your account and have not been updated
              for more than 24 hours. This follows the same inactivity rule used in
              the superadmin inactive shipment tracker.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700"
            aria-label="Close inactive shipment alert"
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="mb-4 flex items-center gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900 border border-amber-200">
            <i className="fa-solid fa-triangle-exclamation text-amber-600" />
            <span>
              {shipments.length} inactive shipment{shipments.length > 1 ? "s" : ""} on your account
            </span>
          </div>

          <div className="space-y-3">
            {previewShipments.map((shipment) => (
              <div
                key={shipment.shipment_id}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">
                      {shipment.tracking_no || shipment.job_order || `Shipment ${shipment.shipment_id}`}
                    </p>
                    <p className="break-words text-sm text-slate-600">
                      {shipment.shipper_name || shipment.receiver_name || "Unnamed shipment"}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.12em] text-slate-500">
                      Status: {shipment.status || "Unknown"}
                    </p>
                  </div>
                  <div className="text-sm text-slate-600 sm:shrink-0 sm:text-right">
                    <p>Last update</p>
                    <p className="font-medium text-slate-800">
                      {formatInactiveShipmentDateTime(
                        shipment.status_updated_at
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {remainingCount > 0 && (
            <p className="mt-4 text-sm text-slate-500">
              And {remainingCount} more inactive shipment{remainingCount > 1 ? "s" : ""}.
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
          >
            Dismiss
          </button>
          {shipmentsRoute ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(shipmentsRoute);
              }}
              className="rounded-2xl bg-blue px-5 py-3 text-sm font-semibold text-white transition hover:opacity-95"
            >
              View Shipments
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InactiveShipmentAlertModal;
